import React, { useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import MainModal from "components/Modal/MainModal";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

const OurFeatures = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "" });
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchFeatures = async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getOurFeatures();
      if (!res.ok) throw new Error("Failed to fetch features");
      const { data } = await res.json();
      setFeatures(data);
    } catch (error) {
      toast.error("Error fetching features.");
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const handleDelete = async (feature) => {
    if (!window.confirm(`Are you sure you want to delete this feature?`)) {
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/cms/delete-our-feature/${feature.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete feature");
      }

      toast.success("Feature deleted successfully!");
      await fetchFeatures();
    } catch (error) {
      toast.error(`Error deleting feature: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  const openEditModal = (feature) => {
    setEditingFeature(feature);
    setFormData({ title: feature.title, description: feature.description });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        id: editingFeature.id,
      };

      const res = await adminApiService.updateOurFeature(payload);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update feature");
      }

      const responseData = await res.json();

      console.log(responseData);

      toast.success("Feature updated successfully!");
      setIsModalOpen(false);
      await fetchFeatures();
      setFormData({ title: "", description: "" });
      setEditingFeature(null);
    } catch (error) {
      toast.error(`Error updating feature: ${error.message}`);
      console.error("Submit error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Our Features Management
              </h1>
              <p className="mt-3 text-base text-gray-600">
                Manage the features displayed on the website
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-primary"></div>
            </div>
          ) : features.length === 0 ? (
            <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
              <div className="rounded-full bg-primary/10 p-6">
                <MdEdit className="h-16 w-16 text-primary" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No Features Available
              </h3>
              <p className="mt-3 text-base text-gray-500">
                There are no features to display at the moment.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <div
                  key={feature.id}
                  className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="mt-3 text-base text-gray-600">
                      {feature.description}
                    </p>
                    <div className="mt-6 flex items-center justify-end space-x-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <button
                        onClick={() => openEditModal(feature)}
                        className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                        aria-label="Edit feature"
                      >
                        <MdEdit size={24} />
                      </button>
                      <button
                        onClick={() => handleDelete(feature)}
                        className="rounded-xl p-2.5 text-red-600 transition-colors hover:bg-red-50"
                        aria-label="Delete feature"
                      >
                        <MdDelete size={24} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MainModal
        isOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
        title="Edit Feature"
        btnTitle="Update Feature"
        actionbtn={handleSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="title"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter feature title..."
            />
          </div>
          <div>
            <label
              htmlFor="description"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              required
              placeholder="Enter feature description..."
            />
            <p className="mt-3 text-sm text-gray-500">
              Provide a detailed description of this feature.
            </p>
          </div>
        </div>
      </MainModal>
    </div>
  );
};

export default OurFeatures;
